import { action, createAsyncAction } from 'typesafe-actions'
import { Dispatch } from 'redux'
import api from 'api/graphql/graphql'
import { RATING_HISTORY_START_DEFAULT } from 'constants/profile'
import { generateDateAtMidnight } from 'utils'
import { sortRatingsByType } from 'api/graphql/data-transforms'
import { RatingHistory, WorldTennisNumber } from 'types/global'

export const RATING_HISTORY_REQUESTED = 'ratings/RATING_HISTORY_REQUESTED'
export const RATING_HISTORY_COMPLETED = 'ratings/RATING_HISTORY_COMPLETED'
export const RATING_HISTORY_REQUEST_FAILED = 'ratings/RATING_HISTORY_REQUEST_FAILED'
export const requestRatingHistory = createAsyncAction(
  RATING_HISTORY_REQUESTED,
  RATING_HISTORY_COMPLETED,
  RATING_HISTORY_REQUEST_FAILED
)<void, RatingHistory[], Error>()

export const EARLIEST_RATING_SET = 'ratings/EARLIEST_RATING_SET'
export const setEarliestRecord = (record: string) => action(EARLIEST_RATING_SET, record)

export const RATING_HISTORY_DATE_RANGE_SET = 'ratings/RATING_HISTORY_DATE_RANGE_SET'
export const setWtnChartRange = (dateRange: { start: string; end: string }) =>
  action(RATING_HISTORY_DATE_RANGE_SET, dateRange)

const fetchAllRatingsForPlayer = async ({
  id,
  startDate,
  endDate,
}: {
  id: string
  startDate: string
  endDate: string
}) => {
  const fetchLimit = 100
  const firstPage = await api.ratingsHistoryQuery({
    playerId: id,
    start: startDate,
    end: endDate,
    limit: fetchLimit,
    skip: 0,
  })
  const totalItems = firstPage.totalItems
  let itemsRequested = fetchLimit

  const promises: Promise<{
    items: WorldTennisNumber[]
    totalItems: number
  }>[] = [Promise.resolve(firstPage)]

  while (itemsRequested < totalItems) {
    promises.push(
      api.ratingsHistoryQuery({
        playerId: id,
        start: startDate,
        end: endDate,
        limit: fetchLimit,
        skip: itemsRequested,
      })
    )

    itemsRequested += fetchLimit
  }

  const results = await Promise.all(promises)
  return sortRatingsByType(
    results
      .map(r => r?.items)
      .filter(Boolean)
      .flat()
  )
}

export const fetchRatingsHistory = (playerIds: string[], start: string, end: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(requestRatingHistory.request())
      const startDate = generateDateAtMidnight({ dateInput: start }).toISOString()
      const endDate = generateDateAtMidnight({ dateInput: end, endOfDay: true }).toISOString()

      const results = await Promise.all(
        playerIds.map(id => {
          return fetchAllRatingsForPlayer({ id, startDate, endDate })
        })
      )

      dispatch(requestRatingHistory.success(results))
    } catch (e) {
      console.warn('Failed to fetch rating results', e && (e as Error)?.message)
      dispatch(requestRatingHistory.failure(e as Error))
    }
  }
}

export const fetchEarliestRecord = (playerIds: string[]) => {
  return async (dispatch: Dispatch) => {
    try {
      let ratings = await Promise.all(
        playerIds.map(id => {
          return api.earliestRatingQuery(id)
        })
      )

      let minDate = new Date(RATING_HISTORY_START_DEFAULT)

      if (ratings[0]?.length) {
        ratings.forEach(rating => {
          if (!rating.length) return
          const earliestRatingDate = new Date(rating[0].ratingDate)
          if (earliestRatingDate < minDate) minDate = earliestRatingDate
        })
      }

      dispatch(setEarliestRecord(minDate.toISOString()))
    } catch (e) {
      console.warn('Failed to fetch earliest record', e && (e as Error)?.message)
    }
  }
}
